import { useMemo, useState } from 'react';
import { NJButton, NJFormItem, NJIconButton } from '@engie-group/fluid-design-system-react';
import { Size2MixInfoGrid, Size2MixNetworkMap, PUC_EDM_GEOMETRY, type NetworkGeometry } from '@shared/size2mix-shell';
import { useUpdatePipe } from './hooks';
import type { MapSelection, TopologyData } from './types';

const TOOLS = [
    { id: 'select', icon: 'near_me', label: 'Sélectionner' },
    { id: 'pipe', icon: 'timeline', label: 'Ajouter une canalisation' },
    { id: 'substation', icon: 'add_home', label: 'Ajouter une sous-station' },
    { id: 'valve', icon: 'radio_button_checked', label: 'Ajouter une vanne' },
    { id: 'delete', icon: 'delete_outline', label: 'Supprimer' },
] as const;

export default function EditorTab({ data }: { data: TopologyData }) {
    const [tool, setTool] = useState<(typeof TOOLS)[number]['id']>('select');
    const [selected, setSelected] = useState<MapSelection | null>(null);
    const [search, setSearch] = useState('');
    const [draftDn, setDraftDn] = useState('');
    const updatePipe = useUpdatePipe();

    const geometry = useMemo<NetworkGeometry>(
        () => ({
            ...PUC_EDM_GEOMETRY,
            pipes: PUC_EDM_GEOMETRY.pipes.map((p) => (data.pipeOverrides[p.id] ? { ...p, dn: data.pipeOverrides[p.id].dn } : p)),
        }),
        [data.pipeOverrides],
    );

    const pipe = selected?.kind === 'pipe' ? geometry.pipes.find((p) => p.id === selected.id) : undefined;
    const substation = selected?.kind === 'substation' ? geometry.substations.find((s) => s.id === selected.id) : undefined;
    const departure = selected?.kind === 'departure' ? data.departures.find((d) => d.departure === selected.id) : undefined;

    const select = (id: string | null, kind: MapSelection['kind'] | null) => {
        setSelected(id && kind ? { id, kind } : null);
        const p = kind === 'pipe' ? geometry.pipes.find((x) => x.id === id) : undefined;
        setDraftDn(p ? String(p.dn) : '');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: 'calc(100vh - 300px)', minHeight: 520 }}>
            <p role="note" style={{ margin: 0, fontSize: 14 }}>Vue illustrative PUC EDM historique : la géométrie et le fond Cesium du réseau test PUC n'ont pas été capturés. Cette vue n'est pas une référence vérifiée.</p>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                <div role="toolbar" aria-label="Outils d'édition" style={{ display: 'flex', gap: 4, padding: 4, border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                    {TOOLS.map((t) => (
                        <NJIconButton key={t.id} icon={t.icon} label={t.label} variant={tool === t.id ? 'primary' : 'tertiary'} scale="sm" onClick={() => setTool(t.id)} />
                    ))}
                </div>
                <div style={{ width: 280 }}>
                    <NJFormItem
                        id="ref-topo-search"
                        label="Rechercher une entité"
                        labelKind="static"
                        iconName="search"
                        value={search}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                    />
                </div>
                <span style={{ flex: 1 }} />
                <span style={{ color: 'var(--nj-semantic-color-text-neutral-secondary-default)', fontSize: 14 }}>
                    {geometry.pipes.length} canalisations · {geometry.substations.length} sous-stations · {geometry.departures.length} départs
                </span>
            </div>

            <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
                <div style={{ flex: 1, border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)' }}>
                    <Size2MixNetworkMap geometry={geometry} colorBy="none" showSubstations showDepartures highlight={search} selectedId={selected?.id} onSelect={select} />
                </div>
                <aside
                    style={{
                        width: 340,
                        padding: 16,
                        overflowY: 'auto',
                        background: 'var(--nj-semantic-color-background-neutral-primary-default)',
                        border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
                    }}
                >
                    {!selected && <p style={{ color: 'var(--nj-semantic-color-text-neutral-secondary-default)' }}>Sélectionnez un élément sur la carte pour afficher ses propriétés.</p>}
                    {pipe && (
                        <>
                            <h3 style={{ marginTop: 0, fontSize: 16 }}>Canalisation {pipe.id}</h3>
                            <Size2MixInfoGrid labelWidth={130} rows={[{ label: 'Longueur', value: `${pipe.len} m` }]} />
                            <form
                                style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    updatePipe.mutate({ id: pipe.id, dn: Number(draftDn) });
                                }}
                            >
                                <NJFormItem
                                    id="ref-topo-dn"
                                    label="Diamètre nominal (DN)"
                                    labelKind="static"
                                    type="number"
                                    value={draftDn}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftDn(e.target.value)}
                                />
                                <NJButton type="submit" label="Enregistrer" disabled={!draftDn || Number(draftDn) === pipe.dn || updatePipe.isPending} />
                            </form>
                        </>
                    )}
                    {substation && (
                        <>
                            <h3 style={{ marginTop: 0, fontSize: 16 }}>Sous-station {substation.id}</h3>
                            <Size2MixInfoGrid
                                labelWidth={150}
                                rows={[
                                    { label: 'Puissance nominale', value: `${substation.kw} kW` },
                                    { label: 'Puissance livrée', value: `${substation.mw} MW` },
                                ]}
                            />
                        </>
                    )}
                    {selected?.kind === 'departure' && (
                        <>
                            <h3 style={{ marginTop: 0, fontSize: 16 }}>Départ {selected.id}</h3>
                            <Size2MixInfoGrid
                                labelWidth={150}
                                rows={[
                                    { label: 'Site', value: departure?.site ?? '-' },
                                    { label: 'Groupe de pompes', value: departure?.pumpGroup ?? '-' },
                                ]}
                            />
                        </>
                    )}
                </aside>
            </div>
        </div>
    );
}
